import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Map,
  FeatureGroup,
  Circle,
  LayersControl,
  TileLayer,
  Rectangle,
  Marker,
  Popup,
  Polygon,
  Tooltip,
} from 'react-leaflet';
import L from 'leaflet';
import { EditControl } from 'react-leaflet-draw';
import leafletImage from 'leaflet-image';
import LeafletLayer from './LeafletLayer';
import cn from 'classnames';

import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';

import 'leaflet-measure-path/leaflet-measure-path.css';
import 'leaflet-measure-path/leaflet-measure-path.js';
import { withStyles } from '@material-ui/styles';
import { Button, TextField } from '@material-ui/core';
import { SketchToolContext } from '../SketchTool/context';
import { setSelectedPopupLayerId, updateLayer } from '../SketchTool/actions';
import { useWindowResize } from '../Hooks/useWindowResize';

export const MEASUREMENT_FONT_SIZE_MIN = 4;
export const MEASUREMENT_FONT_SIZE_MAX = 26;

const styles = theme => {
  const fontSizeClasses = [...Array(MEASUREMENT_FONT_SIZE_MAX - MEASUREMENT_FONT_SIZE_MIN + 1).keys()]
    .map(num => num + MEASUREMENT_FONT_SIZE_MIN)
    .reduce((acc, fontSize) => {
      acc[`measurementFontSize${fontSize}`] = {
        fontSize,
        '& .leaflet-measure-path-measurement': {
          fontSize,
        },
      };

      return acc;
    }, {});

  return fontSizeClasses;
};

const usePrevious = value => {
  const ref = useRef();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
};

const LeafletMap = withStyles(styles)(
  ({
    classes,
    center,
    layers,
    leafletData,
    onAdded,
    onCreated,
    onUpdated,
    onLabelMoved,
    onDeleted,
    onClick,
    mapRef,
    zoomLevel,
    onzoomend,
    onmoveend,
    measurementFontSize = 12,
  }) => {
    const [tempLabelText, setTempLabelText] = useState('');

    const prevLayers = usePrevious(layers);
    const prevLeaflet = usePrevious(leafletData);

    const popupRef = useRef();
    const winSize = useWindowResize();

    const {
      state: { selectedLayerId, selectedPopupLayerId },
      dispatch,
    } = useContext(SketchToolContext);

    const getLeafletLayers = () => (mapRef.current ? mapRef.current.leafletElement._layers : null); // eslint-disable-line

    const handleMarkerClick = (event, layerId) => {
      if (layerId === selectedPopupLayerId) {
        return dispatch(setSelectedPopupLayerId(null));
      }

      dispatch(setSelectedPopupLayerId(layerId));
    };

    const handleLeafletLayerLabelRemoved = (event, layerId) => {
      dispatch(updateLayer(layerId, 'labelLatLng', undefined));
      dispatch(setSelectedPopupLayerId(null));
    };

    const handleLeafletLayerLabelTextUpdate = (event, layerId) => {
      console.log({
        event,
        tempLabelText,
        layerId,
      });
      if (!!tempLabelText) {
        dispatch(updateLayer(layerId, 'labelText', tempLabelText));

        try {
          popupRef.current.leafletElement.options.leaflet.map.closePopup();
        } catch (err) {
          console.log('popup could not be closed');
        }
      }
    };

    useEffect(() => {
      try {
        const leafletLayers = getLeafletLayers();

        // Handle deleted layer
        if (prevLayers && prevLayers.length) {
          const removedIds = prevLayers
            .filter(({ id: prevId }) => !layers.some(({ id: currentId }) => currentId === prevId))
            .map(layer => layer.id);
          Object.keys(leafletLayers).forEach(key => {
            if (removedIds.includes(parseInt(key, 10))) {
              leafletLayers[key].remove();
            }
          });
        }

        // When a layers visibility changes, show/hide it from the map
        layers.forEach(layer => {
          if (!layer.enabled && leafletLayers[layer.id]) {
            leafletLayers[layer.id].remove();
          }
        });

        // If we had leaflet data and it was removed due to canceling the dialog,
        // it won't be present in the layer array, but will still be on the map,
        // so get the previous leaflet and check if it's not in the layer array,
        // if it's not, remove it
        if (prevLeaflet && !leafletData && !layers.some(l => l.id === prevLeaflet.layer._leaflet_id)) {
          // eslint-disable-line
          leafletLayers[prevLeaflet.layer._leaflet_id].remove(); // eslint-disable-line
        }

        // Remove any added leaflet layer that is not tied to a sketch layer
        Object.keys(leafletLayers).map(id => {
          const leafletLayer = leafletLayers[id];
          if (leafletLayer.options && leafletLayer.options.layerId && leafletLayers[leafletLayer.options.layerId]) {
            leafletLayers[leafletLayer.options.layerId].remove();
          }
        });
      } catch (e) {
        console.log('e', e);
      }
    }, [leafletData]);

    if (center) {
      window.map = mapRef.current;
      const mapWidth = mapRef.current?.container.parentNode.clientWidth ?? 1000;
      const mapHeight = mapWidth / 2;
      const mapKey = `${mapWidth}_${mapHeight}`;

      return (
        <div
          className={cn('row-center-center', classes[`measurementFontSize${measurementFontSize}`])}
          style={{
            zIndex: 0,
          }}
        >
          <Map
            ref={mapRef}
            cursor
            center={center}
            zoom={zoomLevel}
            onzoomend={onzoomend}
            onmoveend={onmoveend}
            scrollWheelZoom="center"
            maxZoom={22}
            key={mapKey}
            style={{
              width: mapWidth,
              height: mapHeight,
              //width: '100%',
              //height: ,
              zIndex: 0,
              background: 'transparent',
            }}
            preferCanvas
            id="map"
          >
            <TileLayer
              url={'http://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}'}
              subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
            />
            <FeatureGroup>
              <EditControl
                position="topright"
                cursor
                onCreated={e => onCreated(e)}
                onEdited={e => onUpdated(e)}
                onDeleted={e => onDeleted(e)}
                draw={{
                  polyline: {
                    shapeOptions: { color: 'red' },
                    allowIntersection: false,
                    showLength: true,

                    metric: false,
                    feet: true,
                    maxPoints: 2,
                  },
                  multipolyline: false,
                  polygon: {
                    allowIntersection: true,
                    shapeOptions: { color: 'blue' },
                    edit: false,
                    showLength: true,
                    metric: false,
                    feet: true,
                    showArea: true,
                  },
                  rectangle: false,
                  circle: false,
                  marker: false,
                  circlemarker: false,
                }}
              />
              {layers && layers.length
                ? layers
                    .filter(layer => layer.enabled && layer.layerType !== 'baseLayer')
                    .reverse()
                    .map(layer => {
                      // const markerPosition = [layer.latLngs?.[0].lat, layer.latLngs?.[0].lng];
                      const markerPosition = layer.labelLatLng || { lat: null, lng: null };
                      const isSelected = selectedLayerId === layer.id;
                      const layerKey = `leafletLayer${layer.lastUpdate}${JSON.stringify(layer.latLngs)}`;
                      const markerKey = `leafletLayerMarker-${layer.id}-${JSON.stringify(layer.labelLatLng)}`;

                      return (
                        <>
                          <LeafletLayer
                            key={layerKey}
                            layer={layer}
                            onAdded={e => onAdded(e)}
                            mapRef={mapRef}
                            onClick={onClick}
                            isSelected={isSelected}
                          />
                          {/* {!!markerPosition && ( */}
                          <Marker
                            key={markerKey}
                            position={markerPosition}
                            icon={L.divIcon({ html: layer.labelText, className: 'leaflet-custom-label' })}
                            draggable
                            ondragend={event => {
                              onLabelMoved(event, layer.id);
                            }}
                            onclick={event => {
                              handleMarkerClick(event, layer.id);
                            }}
                          >
                            <Popup
                              onOpen={() => {
                                setTempLabelText(layer.labelText || '');

                                console.log(popupRef);
                              }}
                              ref={popupRef}
                            >
                              <div>
                                <TextField
                                  variant="outlined"
                                  value={tempLabelText}
                                  onChange={event => {
                                    setTempLabelText(event.target.value);
                                  }}
                                />

                                <div style={{ marginTop: 10 }}>
                                  <Button
                                    onClick={event => handleLeafletLayerLabelRemoved(event, layer.id)}
                                    variant="contained"
                                    style={{
                                      backgroundColor: '#f44336',
                                      color: '#fff',
                                      marginRight: 6,
                                    }}
                                  >
                                    Remove
                                  </Button>
                                  <Button
                                    onClick={event => handleLeafletLayerLabelTextUpdate(event, layer.id)}
                                    variant="contained"
                                    style={{
                                      backgroundColor: '#315382',
                                      color: 'white',
                                      marginLeft: 6,
                                    }}
                                  >
                                    Update
                                  </Button>
                                </div>
                              </div>
                            </Popup>
                          </Marker>
                        </>
                      );
                    })
                : null}
              {(() => {
                const baseLayer = layers.find(layer => layer.layerType === 'baseLayer');
                if (baseLayer) {
                  return (
                    <LeafletLayer
                      key={`leafletLayer${baseLayer.lastUpdate}${JSON.stringify(baseLayer.latLngs)}`}
                      layer={baseLayer}
                      onAdded={e => onAdded(e)}
                    />
                  );
                }
              })()}
            </FeatureGroup>
          </Map>
        </div>
      );
    }
    return null;
  },
);

LeafletMap.propTypes = {
  center: PropTypes.object,
  layers: PropTypes.array,
  onUpdated: PropTypes.func,
  onLabelUpdated: PropTypes.func,
  onCreated: PropTypes.func,
  onDeleted: PropTypes.func,
  onClicked: PropTypes.func,
};
export default LeafletMap;
